import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Swords, Trash2, Sun, Home, ChevronRight } from "lucide-react";
import RecChallenges from "@/components/RecChallenges";

const REC = "#F472B6";

const MemberInput = ({ value, onChange, placeholder, testid }) => {
  const [sugs, setSugs] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const search = (q) => {
    clearTimeout(timer.current);
    if (!q.trim()) return setSugs([]);
    timer.current = setTimeout(async () => {
      try { const { data } = await api.get("/members/search", { params: { q } }); setSugs(data); } catch { /* ignore */ }
    }, 250);
  };
  return (
    <div className="relative">
      <input data-testid={testid} value={value} placeholder={placeholder}
        onChange={(e) => { onChange(e.target.value); search(e.target.value); setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm focus:outline-none focus:border-[#F472B6]" />
      {open && sugs.length > 0 && (
        <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-pb-surface border border-pb-border max-h-44 overflow-auto shadow-xl">
          {sugs.map((m) => (
            <button key={m.id} data-testid={`member-sug-${m.id}`} type="button"
              onMouseDown={() => { onChange(m.name); setSugs([]); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-pb-elevated flex items-center justify-between">
              <span>{m.name}</span>
              <span className="text-[9px] uppercase tracking-widest text-zinc-500">{m.membership}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Seg = ({ options, value, onChange, prefix }) => (
  <div className="flex border border-pb-border overflow-hidden">
    {options.map(([key, label, Icon]) => (
      <button key={key} type="button" data-testid={`${prefix}-${key}`} onClick={() => onChange(key)}
        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] font-bold uppercase tracking-widest transition-colors ${value === key ? "bg-[#F472B6] text-black" : "bg-pb-elevated/40 text-zinc-400 hover:text-zinc-200"}`}>
        {Icon && <Icon className="w-3.5 h-3.5" />}{label}
      </button>
    ))}
  </div>
);

export default function RecGames({ onModel }) {
  const { user } = useAuth();
  const [games, setGames] = useState([]);
  const [optIn, setOptIn] = useState(false);
  const [format, setFormat] = useState("singles");
  const [venue, setVenue] = useState("indoor");
  const [partner, setPartner] = useState("");
  const [opp1, setOpp1] = useState("");
  const [opp2, setOpp2] = useState("");
  const [myScore, setMyScore] = useState(11);
  const [oppScore, setOppScore] = useState(7);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/rec-games"); setGames(data.games); setOptIn(data.opt_in); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const toggleOpt = async () => {
    const next = !optIn;
    setOptIn(next);
    try {
      const { data } = await api.post("/rec-games/opt", { enabled: next });
      onModel?.(data.player_model);
      toast.success(next ? "Rec games now count toward your PSI" : "Rec games removed from your PSI");
    } catch (err) { setOptIn(!next); toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/rec-games", {
        format, venue, partner_name: partner, opp1_name: opp1, opp2_name: opp2,
        my_score: Number(myScore), opp_score: Number(oppScore),
      });
      onModel?.(data.player_model);
      toast.success(`Game logged — ${data.game.won ? "W" : "L"} ${data.game.my_score}-${data.game.opp_score}`);
      setOpp1(""); setOpp2(""); setPartner("");
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (g) => {
    try {
      const { data } = await api.delete(`/rec-games/${g.id}`);
      onModel?.(data.player_model);
      toast.success("Game removed");
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="rec-games-section"
      className="border border-[#F472B6]/60 bg-gradient-to-br from-[#F472B6]/10 via-pb-surface/40 to-pb-surface/30 shadow-[0_0_24px_rgba(244,114,182,0.10)] p-6">
      <div className="flex items-center gap-2 mb-1">
        <Swords className="w-5 h-5 text-[#F472B6]" />
        <h3 className="font-display font-bold text-xl uppercase text-[#F472B6]">
          Recreational Games
          <span className="ml-2 bg-[#F472B6] text-black text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 align-middle">Opt-in · Counts toward PSI</span>
        </h3>
      </div>
      <p className="text-sm text-zinc-500 mb-4">Log your rec play — singles or doubles, indoor or outdoor. Games are scored on wins, opponent strength, competitiveness and session consistency. Opt in below and your creation-play results count toward your PSI (log 4+ games in a day for them to score).</p>

      <RecChallenges />

      <button data-testid="rec-opt-toggle" onClick={toggleOpt}
        className={`w-full flex items-center justify-between border px-4 py-3 mb-5 transition-colors ${optIn ? "border-[#F472B6] bg-[#F472B6]/10" : "border-pb-border bg-pb-elevated/40"}`}>
        <span className="text-xs font-bold uppercase tracking-widest text-zinc-300">Count rec games toward my PSI</span>
        <span className={`relative inline-flex h-5 w-10 shrink-0 rounded-full transition-colors ${optIn ? "bg-[#F472B6]" : "bg-pb-border"}`}>
          <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-black transition-transform ${optIn ? "translate-x-5" : "translate-x-0.5"}`} />
        </span>
      </button>

      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <Seg prefix="rec-format" value={format} onChange={setFormat}
          options={[["singles", "Singles", null], ["doubles", "Doubles", null]]} />
        <Seg prefix="rec-venue" value={venue} onChange={setVenue}
          options={[["indoor", "Indoor", Home], ["outdoor", "Outdoor", Sun]]} />
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Member</label>
          <div data-testid="rec-member-name" className="w-full bg-pb-elevated/60 border border-pb-border/60 px-3 py-2.5 text-sm text-zinc-300">{user?.name}</div>
        </div>
        {format === "doubles" && (
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Your Partner</label>
            <MemberInput testid="rec-partner-input" value={partner} onChange={setPartner} placeholder="Partner name (member or guest)" />
          </div>
        )}
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">{format === "doubles" ? "Opponent 1" : "Opponent"}</label>
          <MemberInput testid="rec-opp1-input" value={opp1} onChange={setOpp1} placeholder="Opponent name (member or guest)" />
        </div>
        {format === "doubles" && (
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Opponent 2</label>
            <MemberInput testid="rec-opp2-input" value={opp2} onChange={setOpp2} placeholder="Opponent name (member or guest)" />
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Your Score</label>
          <input data-testid="rec-my-score" type="number" min="0" value={myScore} onChange={(e) => setMyScore(e.target.value)}
            className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 font-mono text-lg focus:outline-none focus:border-[#F472B6]" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Opponent Score</label>
          <input data-testid="rec-opp-score" type="number" min="0" value={oppScore} onChange={(e) => setOppScore(e.target.value)}
            className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 font-mono text-lg focus:outline-none focus:border-[#F472B6]" />
        </div>
      </div>

      <button data-testid="rec-submit" onClick={submit} disabled={busy}
        className="w-full bg-[#F472B6] text-black font-bold uppercase tracking-widest py-3 hover:bg-[#BEF264] active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2">
        {busy ? "Logging…" : <>Log Rec Game <ChevronRight className="w-4 h-4" /></>}
      </button>

      {games.length > 0 && (
        <div className="mt-5 border-t border-pb-border/60 pt-4 space-y-2" data-testid="rec-games-history">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Recent Rec Games</div>
          {games.slice(0, 6).map((g) => (
            <div key={g.id} data-testid={`rec-game-${g.id}`} className="flex items-center gap-3 text-sm border-b border-pb-border/50 pb-2">
              <span className={`font-display font-black w-6 text-center ${g.won ? "text-green-400" : "text-red-400"}`}>{g.won ? "W" : "L"}</span>
              <span className="font-mono text-zinc-300 w-14">{g.my_score}-{g.opp_score}</span>
              <span className={`flex-1 min-w-0 truncate ${g.challenge ? "text-[#F472B6]" : "text-zinc-400"}`} data-testid={g.challenge ? `rec-game-challenge-${g.id}` : undefined}>
                {g.challenge ? "⚔ " : ""}vs {g.opp1_name}{g.opp2_name ? ` & ${g.opp2_name}` : ""}{g.partner_name ? ` · w/ ${g.partner_name}` : ""}
              </span>
              <span className="text-[9px] uppercase tracking-widest text-zinc-600 shrink-0">{g.format} · {g.venue}</span>
              <button data-testid={`rec-delete-${g.id}`} aria-label="Delete game" onClick={() => remove(g)}
                className="text-zinc-600 hover:text-red-400 transition-colors shrink-0"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
